import React, { useState } from 'react';
import { Palette, Image, Video, FileText, Upload, Eye, Download, CheckCircle } from 'lucide-react';
import { colors } from '../../../constants/theme';
import TemplateGallery from './TemplateGallery';
import CreativeEditor from './CreativeEditor';
import BatchGeneration from './BatchGeneration';
import FlyerPreview from './FlyerPreview';

const CreativeAgent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'template' | 'batch' | 'preview' | 'history'>('template');

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-3">
        {/* コンパクトヘッダー */}
        <div className="mb-2">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold" style={{ color: colors.text.primary }}>
              チラシ生成エージェント
            </h2>
            <p className="text-sm" style={{ color: colors.text.secondary }}>
              AIでチラシ・POPを自動生成
            </p>
          </div>
        </div>

        {/* コンパクトステータス */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <StatusCard
            title="今月の生成数"
            value="234"
            change="+12%"
            icon={<Image />}
            color={colors.accent.purple}
          />
          <StatusCard
            title="承認待ち"
            value="18"
            change="5 urgent"
            icon={<Eye />}
            color={colors.accent.red}
          />
          <StatusCard
            title="テンプレート"
            value="56"
            change="+3 new"
            icon={<FileText />}
            color={colors.accent.blue}
          />
          <StatusCard
            title="完了率"
            value="92%"
            change="+5%"
            icon={<CheckCircle />}
            color={colors.ui.success}
          />
        </div>

        {/* タブナビゲーション */}
        <div className="flex space-x-1 mb-3 border-b" style={{ borderColor: colors.ui.border }}>
          <TabButton
            active={activeTab === 'template'}
            onClick={() => setActiveTab('template')}
            label="テンプレート選択"
          />
          <TabButton
            active={activeTab === 'batch'}
            onClick={() => setActiveTab('batch')}
            label="バッチ生成"
          />
          <TabButton
            active={activeTab === 'preview'}
            onClick={() => setActiveTab('preview')}
            label="プレビュー"
          />
          <TabButton
            active={activeTab === 'history'}
            onClick={() => setActiveTab('history')}
            label="生成履歴"
          />
        </div>

        {/* メインコンテンツ */}
        <div className="space-y-6">
          {activeTab === 'template' && (
            <>
              <TemplateGallery />
              <CreativeEditor />
            </>
          )}
          
          {activeTab === 'batch' && (
            <BatchGeneration />
          )}
          
          {activeTab === 'preview' && (
            <FlyerPreview />
          )}
          
          {activeTab === 'history' && (
            <GenerationHistory />
          )}
        </div>
      </div>
    </div>
  );
};

// ステータスカードコンポーネント
interface StatusCardProps {
  title: string;
  value: string;
  change: string;
  icon: React.ReactNode;
  color: string;
}

const StatusCard: React.FC<StatusCardProps> = ({ title, value, change, icon, color }) => {
  return (
    <div 
      className="p-2 rounded-lg border"
      style={{ borderColor: colors.ui.border }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs" style={{ color: colors.text.secondary }}>
          {title}
        </span>
        {React.isValidElement(icon) ? React.cloneElement(icon, { size: 14, style: { color } }) : icon}
      </div>
      <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
        {value}
      </div>
      <div className="text-xs" style={{ color: colors.text.secondary }}>
        {change}
      </div>
    </div>
  );
};

// タブボタンコンポーネント
interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, label }) => {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 text-xs font-medium border-b-2 transition-colors ${
        active 
          ? 'border-purple-500' 
          : 'border-transparent hover:border-gray-300'
      }`}
      style={{ 
        color: active ? colors.accent.purple : colors.text.secondary,
        borderBottomColor: active ? colors.accent.purple : 'transparent'
      }}
    >
      {label}
    </button>
  );
};

// 生成履歴コンポーネント（仮実装）
const GenerationHistory: React.FC = () => {
  const historyItems = [
    {
      id: 1,
      name: '春の新商品POP',
      type: 'POP',
      status: 'completed',
      date: '2025-01-15',
      items: 12
    },
    {
      id: 2,
      name: 'バレンタインフェア チラシ',
      type: 'チラシ',
      status: 'processing',
      date: '2025-01-14',
      items: 8
    },
    {
      id: 3,
      name: '棚札バッチ更新',
      type: '棚札',
      status: 'completed',
      date: '2025-01-13',
      items: 156
    }
  ];

  return (
    <div className="bg-white rounded-lg border" style={{ borderColor: colors.ui.border }}>
      <div className="p-4 border-b" style={{ borderColor: colors.ui.border }}>
        <h3 className="font-semibold" style={{ color: colors.text.primary }}>
          最近の生成履歴
        </h3>
      </div>
      <div className="divide-y" style={{ borderColor: colors.ui.border }}>
        {historyItems.map(item => (
          <div key={item.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
            <div className="flex-1">
              <div className="font-medium mb-1" style={{ color: colors.text.primary }}>
                {item.name}
              </div>
              <div className="text-sm flex items-center space-x-4">
                <span style={{ color: colors.text.secondary }}>{item.type}</span>
                <span style={{ color: colors.text.secondary }}>{item.date}</span>
                <span style={{ color: colors.text.secondary }}>{item.items} アイテム</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <StatusBadge status={item.status} />
              <button 
                className="p-1.5 hover:bg-gray-100 rounded"
                style={{ color: colors.text.secondary }}
                title="ダウンロード"
              >
                <Download size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ステータスバッジ
interface StatusBadgeProps {
  status: 'completed' | 'processing' | 'error';
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const getStatusStyle = () => {
    switch(status) {
      case 'completed':
        return { backgroundColor: '#E8F5F1', color: colors.ui.success };
      case 'processing':
        return { backgroundColor: '#FFF4E6', color: '#FF9800' };
      case 'error':
        return { backgroundColor: '#FFEBEE', color: colors.accent.red };
      default:
        return { backgroundColor: colors.ui.border, color: colors.text.secondary };
    }
  };

  const getStatusLabel = () => {
    switch(status) {
      case 'completed':
        return '完了';
      case 'processing':
        return '処理中';
      case 'error':
        return 'エラー';
      default:
        return '';
    }
  };

  return (
    <span 
      className="px-2 py-1 rounded text-xs font-medium"
      style={getStatusStyle()}
    >
      {getStatusLabel()}
    </span>
  );
};

export default CreativeAgent;